import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';

/** Friendly noun for what we're creating, from the capability (track / image / video / sound / voice). */
const thingFor = (capability: string): string => {
  if (capability.startsWith('image')) return 'image';
  if (capability.startsWith('video')) return 'video';
  if (capability === 'sfx') return 'sound';
  if (capability === 'audio' || capability === 'voice_premium') return 'voice';
  return 'track'; // music / music_*
};

/** True ONLY for the vocal-song capability — that gets an editable LYRICS field in the preview. Instrumental
 *  (`music_instrumental`) and SFX have NO lyrics, so they fall through to the plain prompt/brief editor
 *  (otherwise the user saw an empty "Lyrics" box and the real director brief was hidden + uneditable). */
const isSongCapability = (capability: string): boolean => capability === 'music';

/** The spec the backend emits (`postSpendConfirm`) to gate a premium media generation. */
export interface ConfirmSpendSpec {
  type: 'ConfirmSpend';
  capability: string;
  /** Always 'WP' — kept on the spec so a stored message renders honestly on its own. */
  currency: string;
  cost: number;
  balance: number;
  prompt: string;
  /** Director-refined generation prompt — the human-in-the-loop preview, editable before spending. */
  previewPrompt?: string;
  /** Director-written song lyrics (songs only) — editable before spending; empty → instrumental. */
  previewLyrics?: string;
  musicStyle?: string;
  musicKind?: string;
  mediaStyle?: string;
  cameraMove?: string;
  aspect?: string;
  size?: string;
  durationSec?: number;
}

/** The user's approved (possibly edited) preview text, returned to the resume call. */
export interface ConfirmSpendEdits { previewPrompt?: string; previewLyrics?: string }

export const isConfirmSpendSpec = (spec: unknown): spec is ConfirmSpendSpec =>
  !!spec && (spec as { type?: string }).type === 'ConfirmSpend';

/**
 * In-stream confirmation before we spend credits on a generation — a human-in-the-loop preview of the
 * core context (the generation prompt, and a song's lyrics) that the user can APPROVE, EDIT inline,
 * or ignore, so they never accidentally drain their wallet AND own the output. "Make it" resumes the
 * generation with the approved/edited text; "don't ask again" turns the prompt off.
 */
export const ConfirmSpendCard: React.FC<{
  spec: ConfirmSpendSpec;
  onConfirm: (spec: ConfirmSpendSpec, dontAskAgain: boolean, edits: ConfirmSpendEdits) => void | Promise<void>;
}> = ({ spec, onConfirm }) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [dontAsk, setDontAsk] = useState(false);
  const [progress, setProgress] = useState(0);
  const [prompt, setPrompt] = useState(spec.previewPrompt ?? spec.prompt ?? '');
  const [lyrics, setLyrics] = useState(spec.previewLyrics ?? '');
  const low = spec.balance < spec.cost;
  const thing = thingFor(spec.capability);
  const song = isSongCapability(spec.capability);

  // While generating (onConfirm awaits the full ~20-60 s generation), creep a progress bar toward
  // ~92% so the wait feels alive; real completion (`done`) finishes it.
  useEffect(() => {
    if (!busy) { setProgress(0); return; }
    const id = setInterval(() => setProgress((p) => Math.min(92, p + Math.max(0.4, (92 - p) * 0.05))), 550);
    return () => clearInterval(id);
  }, [busy]);

  const go = async () => {
    if (busy || done) return;
    setBusy(true);
    try {
      await onConfirm(spec, dontAsk, { previewPrompt: prompt.trim(), previewLyrics: song ? lyrics : undefined });
      setDone(true);
    }
    finally { setBusy(false); }
  };

  if (done) return <p className={`mt-2 ${TYPE.control} text-secondary-400`} data-testid="confirm-spend-done">Here it comes…</p>;

  return (
    <div
      className="mt-3 flex w-full max-w-sm flex-col gap-3 rounded-2xl border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-900 px-4 py-3.5 shadow-sm"
      data-testid="confirm-spend"
    >
      <div className={`flex items-center gap-2 ${TYPE.body} text-secondary-800 dark:text-secondary-100`}>
        <Sparkles className="w-4 h-4 shrink-0 text-secondary-400" />
        <span>Uses <strong>{spec.cost} {spec.currency}</strong> <span className="text-secondary-400">·</span> you have {spec.balance}</span>
      </div>
      {low && (
        <p className={`${TYPE.control} text-amber-600 dark:text-amber-400`}>That&apos;s more than your balance — you may need to top up.</p>
      )}

      {/* Human-in-the-loop preview: only the CONTENT the user cares about + can edit (the scene for a
          visual, the lyrics for a song). The technical/style scaffolding (genre, look, size, shape) is
          set via the composer pickers and deliberately NOT shown here. For a SONG we hide the style
          field entirely and preview only the lyrics; the style still rides along in the data. */}
      {!busy && (spec.previewPrompt !== undefined || song) && (
        <div className="flex flex-col gap-2" data-testid="confirm-spend-preview">
          {!song && spec.previewPrompt !== undefined && (
            <label className="flex flex-col gap-1">
              <span className={`${TYPE.label} text-secondary-400`}>What we&apos;ll create</span>
              <textarea
                value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
                data-testid="confirm-spend-prompt"
                className={`w-full resize-y rounded-lg border border-secondary-200 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-800 px-2.5 py-1.5 ${TYPE.control} text-secondary-800 dark:text-secondary-100 focus:border-secondary-400 focus:outline-none`}
              />
            </label>
          )}
          {song && (
            <label className="flex flex-col gap-1">
              <span className={`${TYPE.label} text-secondary-400`}>Lyrics <span className="normal-case text-secondary-400/70">— leave empty for instrumental</span></span>
              <textarea
                value={lyrics} onChange={(e) => setLyrics(e.target.value)} rows={4}
                placeholder="[Verse]&#10;…"
                data-testid="confirm-spend-lyrics"
                className={`w-full resize-y rounded-lg border border-secondary-200 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-800 px-2.5 py-1.5 ${TYPE.control} text-secondary-800 dark:text-secondary-100 focus:border-secondary-400 focus:outline-none`}
              />
            </label>
          )}
        </div>
      )}

      {busy ? (
        // Generating — a live progress bar instead of a static label.
        <div className="space-y-1.5" data-testid="confirm-spend-progress">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary-100 dark:bg-secondary-800">
            <div className="h-full rounded-full bg-secondary-900 dark:bg-white transition-[width] duration-500 ease-out" style={{ width: `${Math.round(progress)}%` }} />
          </div>
          <p className={`${TYPE.control} text-secondary-500 dark:text-secondary-400`}>Creating your {thing}…</p>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <button
            type="button" onClick={go}
            className={`rounded-full bg-secondary-900 dark:bg-white px-4 py-1.5 ${TYPE.control} font-medium text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100`}
          >
            Make it
          </button>
          <span className={`${TYPE.control} text-secondary-400`}>or just keep chatting</span>
        </div>
      )}

      {!busy && (
        <label className={`flex items-center gap-2 ${TYPE.meta} text-secondary-400 cursor-pointer`}>
          <input type="checkbox" checked={dontAsk} onChange={(e) => setDontAsk(e.target.checked)} className="accent-secondary-700" />
          Don&apos;t ask again
        </label>
      )}
    </div>
  );
};

export default ConfirmSpendCard;
